import usePermission from "../../../Hooks/usePermission";
import { useLazyGetFirmsListBasedLocationQuery } from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import { useState, useEffect } from "react";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import {
  useFetchCityQuery,
  useFetchPlayMakerQuery,
  useFetchTownQuery,
} from "../../../store";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import { CustomDropdown } from "../../../Components/atoms";
import { buildExcelConfig } from "../../../Components/Reports/reportExcelDefaults";
import { parentFirmLabel } from "../../../utils/firmTypeStyles";

export default function ReportFirmListBasedLocationPage() {
  const { auth } = useAuth();
  const [city, setCity] = useState("");
  const [town, setTown] = useState("");
  const [activity, setActivity] = useState("");
  const [parsedData, setParsedData] = useState([]);
  const token = auth.accessToken;
  // Sayfa reports.firmLocation ile açılır; bu listeler kendi izinlerini ister —
  // izni yoksa istek atılmaz, süzgeç boş kalır (#414)
  const { hasPermission } = usePermission();

  useFetchPlayMakerQuery(token, { skip: !hasPermission("users.read") });
  const CityResult = useFetchCityQuery(token, { skip: !hasPermission("parameters.cityRead") });
  const TownResult = useFetchTownQuery(token, { skip: !hasPermission("parameters.townRead") });

  const [getFirmListReport, { data: firmListReport, isLoading, isFetching, error }] =
    useLazyGetFirmsListBasedLocationQuery();

  // RTK Query'de `isLoading` YALNIZCA ilk yüklemede true olur; aynı sayfada
  // "Raporu Getir"e tekrar basınca istek gitse bile loading görünmüyordu.
  // Bekleme durumu için `isFetching` de hesaba katılır.
  const isBusy = isLoading || isFetching;

  // Şehir seçenekleri
  const cityOptions =
    CityResult.data?.data?.cities?.map((cityItem) => ({
      id: cityItem._id,
      label: cityItem.cityName,
      value: cityItem.cityName,
    })) || [];

  // İlçe seçenekleri (seçilen şehre göre filtrelenmiş)
  const townOptions =
    TownResult.data?.data?.towns
      ?.filter((townItem) => townItem.city?.cityName === city)
      ?.map((townItem) => ({
        id: townItem._id,
        label: townItem.townName,
        value: townItem.townName,
      })) || [];

  // Aktivite durumu seçenekleri
  const activityOptions = [
    { id: "all", label: "Tümü", value: "" },
    { id: "active", label: "Aktif", value: "active" },
    { id: "inactive", label: "Pasif", value: "inactive" },
  ];

  useEffect(() => {
    if (firmListReport && firmListReport.data) {
      setParsedData(firmListReport.data);
    }
  }, [firmListReport]);

  const handleSearch = () => {
    getFirmListReport({
      token,
      city: city || "undefined",
      town: town || "undefined",
      activity: activity || "undefined",
    });
  };

  const handleClearFilters = () => {
    setCity("");
    setTown("");
    setActivity("");
    setParsedData([]);
  };



  // Excel konfigürasyonu
  const excelConfig = buildExcelConfig({
    sheetName: "Konum Bazlı Firma Raporu",
    widths: [25, 20, 15, 15, 30, 15, 25, 10, 12, 15, 18],
    numericColumns: ["teaDeviceCount", "filterCoffeeDeviceCount", "turkishCoffeeDeviceCount"],
    columnMap: { firmName: 0, mainFirmName: 1, city: 2, town: 3, address: 4, phone: 5, email: 6, status: 7, teaDeviceCount: 8, filterCoffeeDeviceCount: 9, turkishCoffeeDeviceCount: 10 },
  });

  // Dosya adı oluşturma
  const fileName = `Firma_Listesi_Konum_Bazli_${new Date()
    .toLocaleDateString("tr-TR")
    .replace(/\./g, "_")}.xlsx`;

  // Excel verilerini hazırlama
  const excelData = parsedData.map((item) => ({
    "Firma Adı": item.firmName || "-",
    "Ana Firma": parentFirmLabel(item.mainFirmName, item.mainFirmType),
    Şehir: item.city || "-",
    İlçe: item.town || "-",
    Adres: item.address || "-",
    Telefon: item.phone || "-",
    "E-posta": item.email || "-",
    Durum: item.status ? "Aktif" : "Pasif",
    "Çay Cihaz Sayısı": item.teaDeviceCount || 0,
    "Filtre Kahve Cihaz Sayısı": item.filterCoffeeDeviceCount || 0,
    "Türk Kahvesi Cihaz Sayısı": item.turkishCoffeeDeviceCount || 0,
    ...Object.entries(item.productQuotas || {}).reduce(
      (acc, [product, quota]) => {
        acc[`${product} Kotası`] = quota || 0;
        return acc;
      },
      {}
    ),
  }));

  const pdfBase64 = firmListReport?.pdfBase64;

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, firmListReport?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-semibold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  return (
    <ReportLayout
      title="Müşteri Listesi Konum Merkezli Raporu"
      fileName={fileName}
      excelData={excelData}
      excelConfig={excelConfig}
      headerActions={headerActions}
        sidebar={
          <ReportFilterCard
          title="Filtre Seçenekleri"
          description="Konum bazlı firma listesi için filtreleri seçin"
          showDatePickers={false}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isBusy}
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          >
          <div className="grid grid-cols-1 gap-4">
          {/* Şehir */}
          <CustomDropdown
          value={cityOptions.find((opt) => opt.value === city)}
          onChange={(option) => {
          setCity(option?.value || "");
          setTown(""); // Şehir değiştiğinde ilçeyi temizle
          }}
          options={cityOptions}
          placeholder="Şehir seçin..."
          searchPlaceholder="Şehir ara..."
          loading={CityResult.isLoading}
          loadingMessage="Şehirler yükleniyor..."
          emptyMessage="Şehir bulunamadı"
          />

          {/* İlçe */}
          <CustomDropdown
          value={townOptions.find((opt) => opt.value === town)}
          onChange={(option) => setTown(option?.value || "")}
          options={townOptions}
          placeholder={city ? "İlçe seçin..." : "Önce şehir seçin"}
          searchPlaceholder="İlçe ara..."
          loading={TownResult.isLoading}
          loadingMessage="İlçeler yükleniyor..."
          emptyMessage={city ? "İlçe bulunamadı" : "Önce şehir seçin"}
          disabled={!city}
          />

          {/* Aktivite Durumu */}
          <CustomDropdown
          value={activityOptions.find((opt) => opt.value === activity)}
          onChange={(option) => setActivity(option?.value || "")}
          options={activityOptions}
          placeholder="Aktivite durumu seçin..."
          showSearch={false}
          />
          </div>
          </ReportFilterCard>
        }
    >
      {/* Loading State */}
      {isBusy && (
        <ReportLoadingState
          title="Firma Listesi Hazırlanıyor"
          description="Veriler yükleniyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={5}
          skeletonColumns={6}
        />
      )}

      {/* Error State */}
      {error && (
        <ReportErrorState
          title="Hata Oluştu"
          message={error.message || "Rapor yüklenirken bir hata oluştu."}
          icon="warning"
        />
      )}

      {/* PDF önizleme — rapor sunucuda tasarlanmış PDF olarak üretilir */}
      {!isBusy && !error && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`Müşteri Listesi Konum Merkezli Raporu — ${parsedData.length} firma`}
        />
      )}

      {/* Empty State */}
      {!isBusy && !error && !pdfBase64 && (
        <ReportEmptyState
          title="Veri Bulunamadı"
          message="Seçilen kriterlere uygun firma bulunamadı."
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
